import { Card } from '@unit-finance/unit-node-sdk';
import { DateTime } from 'next-auth/providers/kakao';

export { CustomerTokenPurposeEnum } from '@finance/platform';

export const TWO_FACTOR_TEST_CODE = '000001';

export enum AuthPages {
    signIn = '/auth/embedded',
    signOut = '/auth/signout',
    error = '/auth/hbauth'
}

export type HBUserType = 'regular' | 'admin';

export enum LocalStorageKeys {
    KYC_FORM_VALUES = 'kyc-form-values',
    KYC_LAST_COMPLETED_STEP = 'kyc-last-completed-step',
    SHOW_APPLICATION_SUCCESS = 'show-application-success'
}

export enum PaymentMethod {
    MANUAL = 'Manual',
    SWIPE = 'Swipe',
    CONTACTLESS = 'Contactless',
    CHIP_AND_PIN = 'ChipAndPin',
    STORED = 'Stored',
    OTHER = 'Other'
}

export enum DigitalWallet {
    GOOGLE = 'Google',
    APPLE = 'Apple',
    OTHER = 'Other'
}

export type UnitCustomerTokenScope = 'payments-write' | 'accounts' | 'transactions' | 'cards-sensitive' | 'statements';

export enum CardType {
    IndividualVirtualDebitCard = 'individualVirtualDebitCard',
    BusinessVirtualDebitCard = 'businessVirtualDebitCard',
    IndividualPhysicalDebitCard = 'individualDebitCard',
    businessPhysicalDebitCard = 'businessDebitCard'
}

export interface IHBSessionData {
    hbApiVersion: number;
    hbToken: string;
}

export type PermissionName = 'funds' | 'tax_hub' | 'payroll';

export type Permission = {
    name: PermissionName;
};

export enum FeatureFlags {
    FinanceHBFundsKyc = 'hb_funds_kyc_kill_switch',
    BusinessInsuranceBanner = 'finance_business_insurance_banner',
    AutoTaxCategorizationDataPipeline = 'finance_auto_tax_categorization_data_pipeline',
    PricingAssistance = 'pricing_assistance',
    FinancialInsights = 'finance_insights',
    FinancialInsightsCarousel = 'finance_insights_carousel',
    Payroll = 'finance_payroll',
    CapitalReportInTaxHub = 'capital_report_in_tax_hub',
    FinanceExpensesIntroModal = 'finance_expenses_intro_modal',
    FinanceSuspectedFraudMessage = 'finance_suspected_fraud_message',
    ExpensesSourceColumn = 'expenses_source_column',
    BalanceWelcome2025 = 'balance_welcome_2025',
    PayrollW2 = 'payroll_w2',
    RebrandNewFonts = 'rebrand_new_fonts',
    ExpenseUpdatedWorkflow = 'expense_updated_workflow',
    Ten99Stripe2025CsvDocs = '1099_stripe_2025_csv_docs',
    HBPaymentsProcessing = 'finance_hb_payments_v2',
    PayrollAddonSubscriptionBanner = 'payroll_addon_pricing_banner',
    PayrollAddonLandingPage = 'payroll_addon_landing_page',
    PayrollOpenForSubscription = 'payroll_open_for_subscription',
    WidgetLastUpdatedIndication = 'finance_widget_last_updated_indication',
    HidePayrollPaymentsTab = 'hide_payroll_payments_tab',
    NextYearFilterHoneybookPaymentsGraph = 'next_year_filter_honeybook_payments_graph'
}

export enum OfficerTitle {
    CEO = 'CEO',
    COO = 'COO',
    CFO = 'CFO',
    President = 'President',
    BenefitsAdministrationOfficer = 'BenefitsAdministrationOfficer',
    CIO = 'CIO',
    VP = 'VP',
    AVP = 'AVP',
    Treasurer = 'Treasurer',
    Secretary = 'Secretary',
    Controller = 'Controller',
    Manager = 'Manager',
    Partner = 'Partner',
    Member = 'Member'
}

export enum EntityType {
    Corporation = 'Corporation',
    LLC = 'LLC',
    Partnership = 'Partnership',
    SoleProprietor = 'Sole proprietor'
}

export enum UnitVerificationTokenChannel {
    SMS = 'SMS',
    CALL = 'CALL'
}

export enum CardStatus {
    Active = 'Active',
    Inactive = 'Inactive',
    Stolen = 'Stolen',
    Lost = 'Lost',
    Frozen = 'Frozen',
    ClosedByCustomer = 'ClosedByCustomer',
    SuspectedFraud = 'SuspectedFraud'
}
export type TCardStatus = keyof typeof CardStatus;

export interface CardData {
    id: string;
    last4Digits: string;
    fullName: FullName;
    expirationDate: string;
    status: TCardStatus;
    type: Card['type'];
}

export interface FullName {
    first: string;
    last: string;
}

export type DocumentMimeType = 'image/png' | 'image/jpeg' | 'application/pdf';

export type TAccountTX = {
    transactionId: string;
    accountId: string;
};

export type TimeRange = {
    since: string;
    until: string;
};

export type Change<T> = {
    new: T;
    old: T;
};

export type Bucket = {
    id: string;
    savings: number;
    name: string;
    savingsPercentage: string;
    isTaxBucket: boolean;
    icon: string;
    legacySmartTaxRate?: string;
};

export interface IHoneybookPayment {
    payment_amount: number;
    payment_due_date: string;
}

export enum Timeframe {
    MonthToDate = 'monthToDate',
    LastMonth = 'lastMonth',
    QuarterToDate = 'quarterToDate',
    YearToDate = 'yearToDate',
    ThisMonth = 'thisMonth',
    NextMonth = 'nextMonth',
    NextThreeMonths = 'nextThreeMonths',
    ThisYear = 'thisYear',
    LastYear = 'lastYear',
    NextYear = 'nextYear',
    Custom = 'custom'
}

export type BasicExternalAccount = {
    accountMask: string;
    subtype: string;
};

// external account as received from flitter.
export type ExternalAccountData = BasicExternalAccount & {
    officialName: string;
    type: string;
    institutionName: string;
    institutionId: string;
    accountId: string;
    accountName: string;
    currentBalance: number;
    availableBalance: number;
    lastUpdated: DateTime;
};

export type BalanceData = {
    date: Date;
    balance: number;
};

// external account balance details as received from flitter.
export type ExternalAccountBalanceDetailsData = BasicExternalAccount & {
    subtype: string;
    institutionId: string;
    accountId: string;
    accountMask: string;
    balanceData: BalanceData[];
};

export type TaxDocumentData = {
    document_id: string;
    document_name: string;
    type: string;
    year: number;
    status: string;
};

export enum LogLevel {
    Info = 'info',
    Debug = 'debug',
    Warning = 'warn',
    Error = 'error'
}
